import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';
import {
  getTodayInUserTimezone,
  utcDateToUserTimezoneDateString,
  getDayOfWeekInUserTimezone,
  isToday,
  getUTCDateString,
  isSameCalendarDate,
} from '../utils/dateUtils';

export interface ScheduleJob {
  _id: string;
  scheduledDate?: string;
  scheduledTime?: string;
  status: string;
  customerId?: any;
  address?: any;
  serviceType?: any;
  estimatedDuration?: string;
  isOnPaymentHold?: boolean;
  onHoldReason?: string | null;
  acceptedBid?: any;
  [key: string]: any;
}

interface UseScheduleDataReturn {
  scheduledJobs: ScheduleJob[];
  todaysJobs: ScheduleJob[];
  loading: boolean;
  error: string | null;
  refreshSchedules: (force?: boolean) => Promise<void>;
  refreshTodaysJobs: () => void;
  updateJobOptimistically: (jobId: string, updates: Partial<ScheduleJob>) => void;
  updateJobFromResponse: (updatedJob: any) => void;
  addJobOptimistically: (newJob: ScheduleJob) => void;
}

/**
 * Shared hook for managing schedule data (This Week's Schedule and Today's Jobs)
 * This ensures both views use the same data source and stay in sync
 * @param providerId - Optional provider ID (for Admin viewing another provider's schedule)
 */
export const useScheduleData = (providerId?: string): UseScheduleDataReturn => {
  const [scheduledJobs, setScheduledJobs] = useState<ScheduleJob[]>([]);
  const [todaysJobs, setTodaysJobs] = useState<ScheduleJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Debounce refresh calls - if multiple edits happen quickly, batch them
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingRefreshRef = useRef(false);

  const fetchScheduledJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Calculate start of week (Monday) using date utilities that handle timezone correctly
      const todayStr = getTodayInUserTimezone(); // YYYY-MM-DD in user's timezone
      const [todayYear, todayMonth, todayDay] = todayStr.split('-').map(Number);
      // CRITICAL: Use local date for "today" to get correct day of week
      const today = new Date();
      const todayDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc. (local timezone)
      
      console.log('[SCHEDULE-DATA] Calculating week range:', {
        todayStr,
        todayYear,
        todayMonth,
        todayDay,
        todayDayOfWeek,
        dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][todayDayOfWeek],
        providerId
      });
      
      // Calculate start of week (Monday)
      // If today is Sunday (0), we want NEXT Monday (1 day forward, next week)
      // If today is Monday (1), we want THIS Monday (0 days, today)
      // If today is Tuesday (2), we want THIS Monday (1 day back)
      // etc.
      let daysFromMonday;
      if (todayDayOfWeek === 0) {
        // Sunday: Show Monday-Saturday of NEXT week (1 day forward)
        daysFromMonday = -1;
      } else {
        // Monday-Saturday: Calculate days back to THIS week's Monday
        daysFromMonday = todayDayOfWeek - 1;
      }
      
      // Create start of week (Monday) as UTC midnight for the calendar date
      const startOfWeekUTC = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay - daysFromMonday, 0, 0, 0, 0));
      
      // OPTIMIZATION: Only request this week + 1 week buffer (instead of 3 months)
      // Since we only display "This Week's Schedule", we don't need 3 months of data
      // The buffer covers edge cases (e.g., services scheduled for next Monday when viewing Friday)
      // Backend will still calculate next dates for completed services, but we only fetch scheduled jobs for this range
      const endOfWeekUTC = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay - daysFromMonday + 6, 23, 59, 59, 999)); // Saturday
      const extendedEndDate = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay - daysFromMonday + 13, 23, 59, 59, 999)); // Next Saturday (1 week buffer)

      console.log('[SCHEDULE-DATA] Date range:', {
        startOfWeekUTC: startOfWeekUTC.toISOString(),
        extendedEndDate: extendedEndDate.toISOString(),
        daysFromMonday
      });

      // Build query string with optional providerId for Admin
      // Cache-bust (_t) so we always get fresh data; avoids 304 Not Modified and stale schedule after new bookings
      let queryString = `startDate=${startOfWeekUTC.toISOString()}&endDate=${extendedEndDate.toISOString()}&_t=${Date.now()}`;
      if (providerId) {
        queryString += `&providerId=${providerId}`;
      }

      console.log('[SCHEDULE-DATA] Fetching from:', `/service-requests/provider/schedule?${queryString.slice(0, queryString.indexOf('&_t='))}...`);
      const apiStartTime = performance.now();

      const response = await apiService.get(
        `/service-requests/provider/schedule?${queryString}`
      );
      
      const apiTime = performance.now() - apiStartTime;
      console.log(`[SCHEDULE-DATA] API call took ${apiTime.toFixed(2)}ms`);
      
      console.log('[SCHEDULE-DATA] API response:', {
        success: response.success,
        dataLength: Array.isArray(response.data) ? response.data.length : (response.data ? 'not array' : 'null/undefined'),
        error: response.error
      });
      
      if (response.success && response.data) {
        const processStartTime = performance.now();
        const jobs = response.data as ScheduleJob[];
        console.log('[SCHEDULE-DATA] Fetched scheduled jobs:', jobs.length, 'jobs');
        
        const setStateStartTime = performance.now();
        setScheduledJobs(jobs || []);
        const setStateTime = performance.now() - setStateStartTime;
        const processTime = performance.now() - processStartTime;
        console.log(`[SCHEDULE-DATA] Processing took ${processTime.toFixed(2)}ms (setState: ${setStateTime.toFixed(2)}ms)`);
      } else {
        console.error('[SCHEDULE-DATA] Failed to fetch scheduled jobs:', response.error || 'Unknown error');
        setError('Failed to fetch scheduled jobs');
      }
    } catch (err) {
      console.error('[SCHEDULE-DATA] Failed to fetch scheduled jobs:', err);
      setError('Failed to fetch scheduled jobs');
      // Don't clear state on error - keep existing jobs visible
      // This prevents jobs from disappearing when refresh fails (e.g., 502 errors)
      // The existing state will remain until a successful refresh
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  const fetchTodaysJobs = useCallback(() => {
    try {
      // Use the same data source as Weekly Schedule (scheduledJobs)
      // This ensures consistency - both views use the same fresh data
      
      // Get today's date in the user's timezone (browser timezone)
      const todayStr = getTodayInUserTimezone(); // Returns YYYY-MM-DD
      
      // Also get today in UTC for direct comparison (dates are stored as UTC midnight)
      const now = new Date();
      const todayUTCStr = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
      
      console.log('[SCHEDULE-DATA] Filtering today\'s jobs:', {
        todayStr,
        todayUTCStr,
        scheduledJobsCount: scheduledJobs.length
      });
      
      // SINGLE SOURCE OF TRUTH: Trust the backend's scheduledDate field
      // The backend endpoint /provider/schedule already:
      // 1. Calculates effectiveScheduledDate for completed services (if in range)
      // 2. Includes services completed today (even if auto-rescheduled)
      // 3. Returns scheduledDate as effectiveScheduledDate (line 5395)
      // We should NOT add additional filtering logic that conflicts with backend calculations
      
      // Filter scheduledJobs to only include jobs where scheduledDate matches today
      // OR jobs that were completed today (even if auto-rescheduled to future date)
      // The backend already calculated this correctly, but we need to also check completedDate
      const todaysOnly = scheduledJobs.filter((job: ScheduleJob) => {
        // Must have valid status (backend already filtered these)
        if (!['scheduled', 'in-progress', 'completed'].includes(job.status)) {
          console.log('[SCHEDULE-DATA] Job filtered out - invalid status:', {
            jobId: job._id,
            status: job.status,
            scheduledDate: job.scheduledDate
          });
          return false;
        }

        // CRITICAL: Check if service was completed today - if so, include it even if scheduledDate is in future
        // When a service is completed, backend auto-reschedules by updating scheduledDate to next occurrence
        // But completedDate remains set to today, so we check completedDate to show completed services
        const completedDate = (job as any).completedDate;
        if (completedDate) {
          try {
            const completedDateStr = utcDateToUserTimezoneDateString(new Date(completedDate));
            console.log('[SCHEDULE-DATA] Checking completedDate:', {
              jobId: job._id,
              completedDate: completedDate,
              completedDateStr,
              todayStr,
              matches: completedDateStr === todayStr,
              status: job.status
            });
            if (completedDateStr === todayStr) {
              // Service was completed today - include it in Today's Jobs
              console.log('[SCHEDULE-DATA] ✅ Including completed service from today:', {
                jobId: job._id,
                completedDate: completedDate,
                completedDateStr,
                scheduledDate: job.scheduledDate,
                status: job.status,
                note: 'Service completed today - showing in Today\'s Jobs even if scheduledDate is in future'
              });
              return true;
            }
          } catch (err) {
            console.warn('[SCHEDULE-DATA] Error checking completedDate:', err, {
              jobId: job._id,
              completedDate: completedDate
            });
          }
        }
        
        // For non-completed services, check if scheduledDate matches today
        // Must have a scheduledDate (backend already calculated effectiveScheduledDate)
        if (!job?.scheduledDate) {
          console.log('[SCHEDULE-DATA] Job filtered out - no scheduledDate:', {
            jobId: job._id,
            status: job.status,
            completedDate: completedDate
          });
          return false;
        }
        
        // Compare scheduledDate (which is effectiveScheduledDate from backend) with today
        // CRITICAL: Compare calendar dates in the user's timezone, not UTC
        const jobDate = new Date(job.scheduledDate);
        const jobDateInUserTZ = utcDateToUserTimezoneDateString(jobDate);
        const matchesToday = jobDateInUserTZ === todayStr;
        
        if (matchesToday) {
          console.log('[SCHEDULE-DATA] ✅ Including job scheduled for today:', {
            jobId: job._id,
            scheduledDate: job.scheduledDate,
            jobDateInUserTZ,
            todayStr,
            status: job.status
          });
        } else {
          console.log('[SCHEDULE-DATA] Job filtered out - not scheduled for today:', {
            jobId: job._id,
            scheduledDate: job.scheduledDate,
            jobDateInUserTZ,
            todayStr,
            status: job.status
          });
        }
        
        return matchesToday;
      });

      console.log(`[SCHEDULE-DATA] Filtered ${todaysOnly.length} jobs for today from ${scheduledJobs.length} total scheduled jobs`);
      setTodaysJobs(todaysOnly);
    } catch (err) {
      console.error('[SCHEDULE-DATA] Failed to filter today\'s jobs:', err);
      setTodaysJobs([]);
    }
  }, [scheduledJobs]);

  // Fetch scheduled jobs on mount and when providerId changes
  useEffect(() => {
    fetchScheduledJobs();
  }, [fetchScheduledJobs, providerId]);

  // Re-filter Today's Jobs whenever scheduledJobs changes
  // This ensures Today's Jobs always reflects the latest data from the same source as Weekly Schedule
  useEffect(() => {
    console.log('[SCHEDULE-DATA] scheduledJobs changed, filtering today\'s jobs...');
    fetchTodaysJobs();
  }, [scheduledJobs, fetchTodaysJobs]);

  const refreshSchedules = useCallback(async (force: boolean = false) => {
    // Clear any pending debounced refresh
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    
    // If a refresh is already in progress
    if (pendingRefreshRef.current) {
      if (force) {
        // Force mode: immediately cancel pending refresh (no wait)
        console.log('[SCHEDULE-DATA] Force refresh - canceling pending refresh immediately');
        pendingRefreshRef.current = false;
      } else {
        // Non-force mode: wait briefly then start new one
        console.log('[SCHEDULE-DATA] Refresh already in progress, waiting briefly then starting new one...');
        // Wait just 50ms (reduced from 100ms) - if still pending, cancel and start fresh
        let retries = 0;
        while (pendingRefreshRef.current && retries < 1) {
          await new Promise(resolve => setTimeout(resolve, 50));
          retries++;
        }
        // If still pending after brief wait, cancel it and start fresh
        if (pendingRefreshRef.current) {
          console.log('[SCHEDULE-DATA] Previous refresh still pending, canceling and starting fresh');
          pendingRefreshRef.current = false;
        }
      }
    }
    
    pendingRefreshRef.current = true;
    try {
      console.log('[SCHEDULE-DATA] Starting refresh...');
      await fetchScheduledJobs();
      console.log('[SCHEDULE-DATA] Refresh completed successfully');
      // fetchTodaysJobs will be called automatically via useEffect
    } catch (error) {
      console.error('[SCHEDULE-DATA] Refresh failed:', error);
      throw error; // Re-throw so caller knows it failed
    } finally {
      pendingRefreshRef.current = false;
    }
  }, [fetchScheduledJobs]);

  // Debounced refresh - batches multiple rapid calls into one
  // Reduced timeout for faster updates (200ms instead of 500ms)
  const debouncedRefreshSchedules = useCallback(() => {
    // Clear existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    // Set new timeout - wait 200ms for more edits (reduced from 500ms for faster updates)
    refreshTimeoutRef.current = setTimeout(() => {
      refreshSchedules();
      refreshTimeoutRef.current = null;
    }, 200);
  }, [refreshSchedules]);

  // Optimistic update - immediately update local state before server refresh
  const updateJobOptimistically = useCallback((jobId: string, updates: Partial<ScheduleJob>) => {
    setScheduledJobs(prevJobs => {
      const updated = prevJobs.map(job => {
        if (job._id === jobId) {
          return { ...job, ...updates };
        }
        return job;
      });
      return updated;
    });
    
    // Note: Refresh is handled by caller (immediate refresh, not debounced)
    // This allows the caller to control when refresh happens
  }, []);

  // Update job from backend response - uses authoritative data from POST response
  // Simple replacement - no merging needed since POST returns same format as GET
  const updateJobFromResponse = useCallback((updatedJobFromServer: any) => {
    console.log('[SCHEDULE-DATA] updateJobFromResponse called with:', {
      jobId: updatedJobFromServer._id?.toString() || updatedJobFromServer.id?.toString(),
      scheduledDate: updatedJobFromServer.scheduledDate,
      hasCustomerId: !!updatedJobFromServer.customerId,
      hasAcceptedBidId: !!updatedJobFromServer.acceptedBidId,
    });
    
    setScheduledJobs(prev => {
      const jobId = updatedJobFromServer._id?.toString() || updatedJobFromServer.id?.toString();
      if (!jobId) {
        console.warn('[SCHEDULE-DATA] Cannot update job - missing ID:', updatedJobFromServer);
        return prev;
      }
      
      const beforeCount = prev.length;
      const updated = prev.map(job => {
        const currentJobId = (job._id?.toString() || job.id?.toString());
        if (currentJobId === jobId) {
          console.log('[SCHEDULE-DATA] Replacing job in state:', {
            oldScheduledDate: job.scheduledDate,
            newScheduledDate: updatedJobFromServer.scheduledDate,
          });
          return updatedJobFromServer;
        }
        return job;
      });
      
      const found = updated.some(j => (j._id?.toString() || j.id?.toString()) === jobId);
      console.log('[SCHEDULE-DATA] State update result:', {
        beforeCount,
        afterCount: updated.length,
        jobFound: found,
        jobId,
      });
      
      return updated;
    });
  }, []);

  // Add job optimistically - immediately add to schedule before refresh completes
  const addJobOptimistically = useCallback((newJob: ScheduleJob) => {
    console.log('[SCHEDULE-DATA] addJobOptimistically called with:', {
      jobId: newJob._id?.toString() || newJob.id?.toString(),
      scheduledDate: newJob.scheduledDate,
      hasCustomerId: !!newJob.customerId,
    });
    
    setScheduledJobs(prev => {
      const jobId = newJob._id?.toString() || newJob.id?.toString();
      if (!jobId) {
        console.warn('[SCHEDULE-DATA] Cannot add job - missing ID:', newJob);
        return prev;
      }
      
      // Check if job already exists
      const exists = prev.some(j => (j._id?.toString() || j.id?.toString()) === jobId);
      if (exists) {
        console.log('[SCHEDULE-DATA] Job already exists, updating instead:', jobId);
        return prev.map(job => {
          const currentJobId = (job._id?.toString() || job.id?.toString());
          return currentJobId === jobId ? newJob : job;
        });
      }
      
      // Add new job to the list
      console.log('[SCHEDULE-DATA] Adding new job optimistically:', {
        jobId,
        scheduledDate: newJob.scheduledDate,
        beforeCount: prev.length,
      });
      
      // Add to the list and sort by scheduledDate
      const updated = [...prev, newJob].sort((a, b) => {
        const dateA = a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0;
        const dateB = b.scheduledDate ? new Date(b.scheduledDate).getTime() : 0;
        return dateA - dateB;
      });
      
      return updated;
    });
  }, []);

  const refreshTodaysJobs = useCallback(() => {
    fetchTodaysJobs();
  }, [fetchTodaysJobs]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  return {
    scheduledJobs,
    todaysJobs,
    loading,
    error,
    refreshSchedules,
    refreshTodaysJobs,
    updateJobOptimistically,
    updateJobFromResponse,
    addJobOptimistically,
  };
};

